import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const PaydayLoans = () => {
  const paydayFeatures = [
    {
      icon: <Zap className="w-8 h-8 text-green-600" />,
      title: "Same-Day Approval",
      description: "Get approved within minutes and receive funds the same day"
    },
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: "No Credit Check",
      description: "Bad credit? No problem. We focus on your ability to repay"
    },
    {
      icon: <DollarSign className="w-8 h-8 text-purple-600" />,
      title: "Up to $1,000",
      description: "Borrow what you need to cover unexpected expenses"
    },
    {
      icon: <Clock className="w-8 h-8 text-orange-600" />,
      title: "Quick Process",
      description: "Simple online application takes just minutes to complete"
    }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Apply Online",
      description: "Complete our simple 3-minute application form with basic information"
    },
    {
      step: "2", 
      title: "Get Approved",
      description: "Receive instant approval decision based on your income and ability to repay"
    },
    {
      step: "3",
      title: "Receive Funds",
      description: "Get your money deposited directly into your bank account same day"
    }
  ];

  const requirements = [
    "Be at least 18 years old",
    "Have a steady source of income",
    "Own an active checking account",
    "Provide valid contact information",
    "Be a US citizen or permanent resident"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-green-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-green-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="font-semibold">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-green-200 transition-colors">Personal Loans</Link>
            <Link to="/how-it-works" className="hover:text-green-200 transition-colors">How It Works</Link>
            <Link to="/faq" className="hover:text-green-200 transition-colors">FAQ</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Fast Payday Loans Louisiana
              </h2>
              <p className="text-xl md:text-2xl mb-8 opacity-90">
                Get up to $1,000 in your account today. Same-day approval with no credit check required. QuickLoanPro makes payday loans simple and fast.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Zap className="w-5 h-5 mr-2" />
                  Same-Day Funding
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <Shield className="w-5 h-5 mr-2" />
                  No Credit Check
                </Badge>
                <Badge className="bg-white/20 text-white text-lg px-4 py-2">
                  <DollarSign className="w-5 h-5 mr-2" />
                  Up to $1,000
                </Badge>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Apply for Payday Loan
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white hover:text-green-600 font-bold text-xl px-8 py-4"
                >
                  Check Requirements
                </Button>
              </div>
            </div>
            <div className="hidden md:block">
              <img 
                src="/images/payday_loans_1.png" 
                alt="QuickLoanPro Payday Loans Louisiana"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Why Choose Our Payday Loans?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paydayFeatures.map((feature, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-2 border-green-200 bg-green-50">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            How Payday Loans Work
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorks.map((step, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-green-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6 text-gray-900">
                Payday Loan Requirements
              </h2>
              <p className="text-gray-700 mb-6">
                Our payday loan requirements are simple and straightforward. Most applicants can qualify with basic documentation and proof of income.
              </p>
              <ul className="space-y-3">
                {requirements.map((requirement, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Button 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4"
                  onClick={() => window.open('https://quickloanpro.com/', '_blank')}
                >
                  Check If You Qualify
                </Button>
              </div>
            </div>
            <div>
              <img 
                src="/images/payday_loans_2.jpeg" 
                alt="Payday Loan Requirements"
                className="rounded-lg shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Explore More Financial Solutions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Personal Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Flexible loans for larger expenses</p>
                <Link to="/personal-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Calculator className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Loan Calculator</h3>
                <p className="text-sm text-gray-600 mb-4">Calculate your loan payments</p>
                <Link to="/loan-calculator">
                  <Button variant="outline" size="sm">Calculate</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <BookOpen className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">How It Works</h3>
                <p className="text-sm text-gray-600 mb-4">Learn about our process</p>
                <Link to="/how-it-works">
                  <Button variant="outline" size="sm">Read Guide</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">FAQ</h3>
                <p className="text-sm text-gray-600 mb-4">Common questions answered</p>
                <Link to="/faq">
                  <Button variant="outline" size="sm">View FAQ</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Need Cash Today? Apply Now!
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get your payday loan approved in minutes. Same-day funding available for qualified applicants.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Apply for Payday Loan Now
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-green-600 font-bold text-xl px-8 py-4"
            >
              Call +1 (504) 438-1753
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for payday loans and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaydayLoans;